import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';

/**
 * Remote image for the events surfaces — the events-palette twin of `app-attraction-image`.
 *
 * Same convention as the rest of the app: a plain `<img>` with `(load)`/`(error)`, an opacity
 * fade-in, and `assets/ImageBroken.svg` on failure. Two things differ from the attractions version,
 * both because event media is genuinely optional: a null `url` is a first-class state (7 dev
 * documents ship an empty `mediaItems` array, and `heroImageUrl` returns null for a video with no
 * poster), and the shimmer tint comes from `--ev-*`.
 */
@Component({
    selector: 'app-event-image',
    host: { '[class.loading]': 'showShimmer' },
    template: `
    <img *ngIf="hasUrl" [src]="src" [alt]="alt" (load)="onLoad()" (error)="onError()"
         [style.opacity]="loaded ? 1 : 0" loading="lazy" />
  `,
    styles: [`
    :host {
      display: block;
      overflow: hidden;
      background: var(--ev-surface, #232323);
    }
    /* Pulses the host tint only — never position or opacity — so a card whose image is still
       loading does not shift the layout around it. */
    :host.loading {
      animation: evImageShimmer 1.4s ease-in-out infinite;
    }
    @keyframes evImageShimmer {
      0%, 100% { background-color: var(--ev-shimmer-tint, rgba(255, 255, 255, 0.08)); }
      50%      { background-color: var(--ev-surface, #232323); }
    }
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
      transition: opacity 0.2s ease;
    }
  `],
    standalone: false
})
export class EventImageComponent implements OnChanges {
  @Input() url: string | null = null;
  @Input() alt = '';

  loaded = false;
  errored = false;

  ngOnChanges(changes: SimpleChanges): void {
    // Re-validate ONLY when the URL itself changes. A change to a sibling input (e.g. `alt`, which
    // is bound to a translated string on some call sites) must not reset `loaded`: the <img> src is
    // unchanged, so the browser never re-fires `load` and the image would stay at opacity 0.
    if (changes['url']) {
      this.loaded = false;
      this.errored = false;
    }
  }

  get hasUrl(): boolean {
    return !!this.url && this.url.trim().length > 0;
  }

  /**
   * No shimmer for an event that ships no media: there is nothing in flight to wait for, so pulsing
   * would promise an image that is never going to arrive. The placeholder just sits there.
   */
  get showShimmer(): boolean {
    return !this.loaded && this.hasUrl;
  }

  /**
   * The broken-image glyph is for an image that FAILED, not for an event that never had one.
   *
   * An event with no media at all — 13 dev documents, and any event an admin saves without a photo
   * — renders no `<img>` and just shows the surface tint, because telling a guest something is
   * broken when nothing is would be a lie. A URL that 404s still gets the glyph: there, something
   * really is wrong and it should be visible.
   */
  get src(): string {
    return this.errored ? 'assets/ImageBroken.svg' : this.url!;
  }

  onLoad(): void {
    this.loaded = true;
  }

  onError(): void {
    this.errored = true;
    this.loaded = true;
  }
}
